import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { LogoMark } from "./LogoMark";

const MANDALA =
  "https://images.unsplash.com/photo-1659738943702-a22ddb8f87f1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHxfHxhYnN0cmFjdCUyMGdvbGQlMjBnZW9tZXRyaWMlMjBtYW5kYWxhJTIwbWluaW1hbCUyMHN1YnRsZSUyMGJsYWNrJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3OTI0MDY1Mzl8MA&ixlib=rb-4.1.0&q=85";

const lines = [
  { text: "Comprendere ciò che si ripete.", cls: "text-sand" },
  { text: "Dare spazio a nuove possibilità.", cls: "italic text-gold" },
];

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-[0.55]"
        style={{
          background:
            "radial-gradient(ellipse 70% 55% at 78% 42%, rgba(212,175,55,0.12), transparent 60%), radial-gradient(ellipse 50% 40% at 15% 85%, rgba(184,93,67,0.08), transparent 60%)",
        }}
      />

      <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto w-full pt-32 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative">
        <motion.div className="lg:col-span-7" style={{ y: yText, opacity }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.8, rotate: -30 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="mb-10"
          >
            <LogoMark size={64} />
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="text-xs uppercase tracking-[0.35em] text-gold font-semibold mb-8"
            data-testid="hero-overline"
          >
            Psicologo · Psicoterapeuta in formazione junghiana — Torino
          </motion.p>

          <h1 className="font-serif tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-7xl" data-testid="hero-heading">
            {lines.map((l, i) => (
              <MaskedLine key={l.text} delay={0.45 + i * 0.18} className={l.cls}>
                {l.text}
              </MaskedLine>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.25 }}
            className="mt-9 max-w-xl text-base sm:text-lg text-sand-muted leading-relaxed"
            data-testid="hero-subtitle"
          >
            Uno spazio di ascolto psicologico a Torino e online, per esplorare le difficoltà del
            presente, comprendere i propri schemi interiori e costruire un rapporto più consapevole
            con sé stessi e con gli altri.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.45 }}
            className="mt-11 flex flex-wrap gap-4"
          >
            <button
              data-testid="hero-cta-book-appointment"
              onClick={() => window.__lenis?.scrollTo("#contatti", { offset: -72, duration: 1.4 })}
              className="group relative overflow-hidden text-sm font-semibold text-ink bg-gold px-8 py-4 rounded-full transition-transform duration-300 hover:scale-[1.03] active:scale-95"
            >
              <span className="relative z-10">Richiedi un colloquio</span>
              <span className="absolute inset-0 bg-sand translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]" />
            </button>
            <button
              data-testid="hero-cta-explore-approach"
              onClick={() => window.__lenis?.scrollTo("#approccio", { offset: -72, duration: 1.4 })}
              className="text-sm font-semibold text-sand border border-gold/40 hover:border-gold px-8 py-4 rounded-full transition-colors duration-300 hover:bg-gold/10"
            >
              Scopri l'approccio
            </button>
          </motion.div>
        </motion.div>

        <motion.div
          className="lg:col-span-5 hidden lg:flex justify-center"
          style={{ y: yImg }}
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="relative" data-testid="hero-mandala">
            <div className="mandala-spin absolute -inset-10 rounded-full border border-gold/20" />
            <div className="mandala-spin absolute -inset-20 rounded-full border border-gold/10" style={{ animationDirection: "reverse", animationDuration: "140s" }} />
            <div className="w-[380px] h-[380px] rounded-full overflow-hidden border border-gold/30 shadow-[0_0_120px_rgba(212,175,55,0.15)]">
              <img
                src={MANDALA}
                alt="Simbolo junghiano del Sé — mandala dorato"
                fetchPriority="high"
                decoding="async"
                className="w-full h-full object-cover scale-110"
              />
            </div>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-sand-faint"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Scorri</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}>
          <ArrowDown size={16} />
        </motion.span>
      </motion.div>
    </section>
  );
};
